import React from 'react';
import { Empty, Spin, Typography } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { getSessionId } from './sessionIdentity.js';
import SessionRow from './SessionRow.jsx';
import SessionVirtualCollection from './SessionVirtualCollection.jsx';

const { Text } = Typography;
const ARCHIVE_CONFIRM_RESET_MS = 2200;
const SESSION_GROUP_ORDER = ['今天', '最近7天', '更早'];
const DEFAULT_SESSION_ROWS_PROPS = {
  activeSessionRunning: false,
  continuationError: null,
  continuationKey: '',
  hasMore: false,
  loading: false,
  loadingMore: false,
  loadingSessionId: '',
  nested: false,
  paneCompact: false,
  runningSessionIds: [],
  sessions: [],
  scopeKey: 'sessions',
  scrollElement: null,
  sessionFactsById: new Map(),
  showGroups: true,
  showFullTitle: false,
  showTimestamp: false,
};

function resolveSessionTime(session) {
  return [
    session?.updatedAt,
    session?.updated_at,
    session?.createdAt,
    session?.created_at,
  ].find(Boolean);
}

function isSameLocalDay(first, second) {
  const firstParts = [first.getFullYear(), first.getMonth(), first.getDate()];
  const secondParts = [second.getFullYear(), second.getMonth(), second.getDate()];
  return firstParts.every((value, index) => value === secondParts[index]);
}

function resolveSessionGroupLabel(session) {
  const rawTime = resolveSessionTime(session);
  if (!rawTime) {
    return '更早';
  }
  const date = new Date(rawTime);
  if (Number.isNaN(date.getTime())) {
    return '更早';
  }
  const now = new Date();
  if (isSameLocalDay(now, date)) {
    return '今天';
  }
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000));
  return diffDays >= 0 && diffDays < 7 ? '最近7天' : '更早';
}

function groupSessionsByDate(sessions) {
  const groups = sessions.reduce((acc, session) => {
    const label = resolveSessionGroupLabel(session);
    const target = acc.find((item) => item.label === label);
    if (target) {
      target.sessions.push(session);
      return acc;
    }
    return [...acc, { label, sessions: [session] }];
  }, []);
  return SESSION_GROUP_ORDER
    .map((label) => groups.find((entry) => entry.label === label))
    .filter(Boolean);
}

function LoadingRows({ nested }) {
  return (
    <div style={{ display: 'grid', minHeight: nested ? 64 : 96, placeItems: 'center' }}>
      <Spin size="small" />
    </div>
  );
}

function EmptyRows({ emptySessionsLabel, nested, themeTokens }) {
  if (nested) {
    return (
      <Text style={{ color: themeTokens.textSecondary, padding: '0 0 2px 2px' }} type="secondary">
        {emptySessionsLabel}
      </Text>
    );
  }
  return (
    <Empty
      image={<MessageOutlined style={{ fontSize: 24, color: themeTokens.textMuted }} />}
      styles={{ image: { height: 32 } }}
      description={emptySessionsLabel}
    />
  );
}

export default function SessionRows(props) {
  const {
    activeSessionId,
    activeSessionRunning,
    continuationError,
    continuationKey,
    emptySessionsLabel,
    hasMore,
    loading,
    loadingMore,
    loadingSessionId,
    nested,
    onArchiveSession,
    onLoadMore,
    onPinSession,
    onRenameSession,
    onSelectSession,
    paneCompact,
    runningSessionIds,
    scopeKey,
    scrollElement,
    sessions,
    sessionFactsById,
    showGroups,
    showFullTitle,
    showTimestamp,
    themeTokens,
  } = { ...DEFAULT_SESSION_ROWS_PROPS, ...props };
  const [pendingArchiveSessionId, setPendingArchiveSessionId] = React.useState('');
  const [archiveSubmittingSessionId, setArchiveSubmittingSessionId] = React.useState('');
  const [pinSubmittingSessionId, setPinSubmittingSessionId] = React.useState('');
  const [editingSessionId, setEditingSessionId] = React.useState('');
  const [editingTitle, setEditingTitle] = React.useState('');

  React.useEffect(() => {
    if (!pendingArchiveSessionId) {
      return undefined;
    }
    const timer = setTimeout(() => setPendingArchiveSessionId(''), ARCHIVE_CONFIRM_RESET_MS);
    return () => clearTimeout(timer);
  }, [pendingArchiveSessionId]);

  React.useEffect(() => {
    const exists = sessions.some(
      (session) => getSessionId(session) === pendingArchiveSessionId,
    );
    if (pendingArchiveSessionId && !exists) {
      setPendingArchiveSessionId('');
    }
  }, [pendingArchiveSessionId, sessions]);

  React.useEffect(() => {
    const exists = sessions.some((session) => getSessionId(session) === editingSessionId);
    if (editingSessionId && !exists) {
      setEditingSessionId('');
      setEditingTitle('');
    }
  }, [editingSessionId, sessions]);

  const handleArchiveClick = async (session) => {
    const sessionId = String(getSessionId(session) || '').trim();
    if (!sessionId || archiveSubmittingSessionId === sessionId) {
      return;
    }
    if (pendingArchiveSessionId === sessionId) {
      setPendingArchiveSessionId('');
      setArchiveSubmittingSessionId(sessionId);
      try {
        await onArchiveSession?.(session);
      } finally {
        setArchiveSubmittingSessionId('');
      }
      return;
    }
    setPendingArchiveSessionId(sessionId);
  };

  const handleRenameStart = (session) => {
    const sessionId = String(getSessionId(session) || '').trim();
    if (!sessionId) {
      return;
    }
    setPendingArchiveSessionId('');
    setEditingSessionId(sessionId);
    setEditingTitle(String(session?.title || '').trim());
  };

  const handleRenameCancel = () => {
    setEditingSessionId('');
    setEditingTitle('');
  };

  const handleRenameConfirm = (session) => {
    const sessionId = String(getSessionId(session) || '').trim();
    const nextTitle = String(editingTitle || '').trim();
    if (!sessionId || !nextTitle) {
      handleRenameCancel();
      return;
    }
    onRenameSession?.(session, nextTitle);
    handleRenameCancel();
  };

  const handlePinClick = async (session) => {
    const sessionId = String(getSessionId(session) || '').trim();
    if (!sessionId || pinSubmittingSessionId === sessionId) {
      return;
    }
    setPinSubmittingSessionId(sessionId);
    try {
      await onPinSession?.(session);
    } finally {
      setPinSubmittingSessionId('');
    }
  };

  const pinAction = typeof onPinSession === 'function' ? handlePinClick : undefined;
  if (loading) {
    return <LoadingRows nested={nested} />;
  }
  if (sessions.length === 0) {
    return (
      <EmptyRows
        emptySessionsLabel={emptySessionsLabel}
        nested={nested}
        themeTokens={themeTokens}
      />
    );
  }

  const activeSessions = sessions.filter(
    (item) => String(item?.status || 'active') !== 'archived',
  );
  const groupedSessions = showGroups
    ? groupSessionsByDate(activeSessions)
    : [{ label: '', sessions: activeSessions }];
  const collectionLayout = { gap: nested ? 6 : 8 };
  const renderSession = (session, index) => (
    <div className="flare-staged-entry" style={{ '--flare-entry-stage': index }}>
      <SessionRow
        activeSessionId={activeSessionId}
        activeSessionRunning={activeSessionRunning}
        archiveSubmittingSessionId={archiveSubmittingSessionId}
        editingSessionId={editingSessionId}
        editingTitle={editingTitle}
        factLabels={sessionFactsById instanceof Map
          ? sessionFactsById.get(getSessionId(session)) : []}
        loadingSessionId={loadingSessionId}
        nested={nested}
        onArchiveClick={handleArchiveClick}
        onPinClick={pinAction}
        onEditingTitleChange={setEditingTitle}
        onRenameCancel={handleRenameCancel}
        onRenameConfirm={handleRenameConfirm}
        onRenameStart={handleRenameStart}
        onSelectSession={onSelectSession}
        paneCompact={paneCompact}
        pendingArchiveSessionId={pendingArchiveSessionId}
        pinSubmittingSessionId={pinSubmittingSessionId}
        runningSessionIds={runningSessionIds}
        session={session}
        showFullTitle={showFullTitle}
        showTimestamp={showTimestamp}
        themeTokens={themeTokens}
      />
    </div>
  );
  if (!scrollElement) {
    const acceptedSession = activeSessions.find(
      session => getSessionId(session) === activeSessionId,
    ) ?? activeSessions[0];
    return acceptedSession ? renderSession(acceptedSession, 0) : null;
  }
  return (
    <SessionVirtualCollection
      continuationError={continuationError}
      continuationKey={continuationKey}
      gap={collectionLayout.gap}
      groups={groupedSessions}
      hasMore={hasMore}
      loadingMore={loadingMore}
      nested={nested}
      onLoadMore={onLoadMore}
      renderSession={renderSession}
      scopeKey={scopeKey}
      scrollElement={scrollElement}
      themeTokens={themeTokens}
    />
  );
}
